import { clearAutotraderSession, initAutotraderSession, autotraderFetch } from './autotrader-session';
import { supabase } from './supabaseClient';
import { addToken, removeToken, getTokenList, removeAllTokens, get as getStorage, set as setStorage, setTokenList, remove as removeStorageKey } from '../common/utils/storageManager';
import { parseQueryString } from '../common/utils/tools';
import { getLanguage } from './lang';
import GTM from './gtm';

const DerivAPI = require('deriv-live-api-v2');

const { LiveApi, OAuth } = DerivAPI;

export const AppConstants = Object.freeze({
    STORAGE_ACTIVE_TOKEN: 'activeToken',
    STORAGE_PROMOTER    : 'promoter',
});

export const V4_STORAGE = Object.freeze({
    ACCOUNT_ID   : 'tradingbots.v4.accountId',
    CODE_VERIFIER: 'tradingbots.v4.codeVerifier',
    STATE        : 'tradingbots.v4.state',
    AUTH_MODE    : 'tradingbots.v4.authMode',
    ACCOUNTS     : 'tradingbots.v4.accounts',
});

function ssSet(key, value) {
    try {
        sessionStorage.setItem(key, value);
    } catch (e) {
        /* ignore */
    }
}
function ssGet(key) {
    try {
        return sessionStorage.getItem(key);
    } catch (e) {
        return null;
    }
}
function ssRemove(key) {
    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        /* ignore */
    }
}

const V4_CLIENT_ID = '33o4eyzdf5iX6FmMuMqtx';
const V4_APP_ID = '37985';
const V4_REDIRECT_URI = 'https://legacy.tradingbots.pro';

function apiUrl(path) {
    const base = typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function fetchJson(url, options = {}) {
    const res = await fetch(url, {
        ...options,
        headers: {
            Accept: 'application/json',
            ...(options.headers || {}),
        },
    });
    const text = await res.text();
    let data = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch (e) {
        data = { error: 'Invalid response' };
    }
    if (!res.ok && !data.error) {
        data.error = `Request failed (${res.status})`;
    }
    return { ok: res.ok, status: res.status, data };
}

export function clearV4Session() {
    removeStorageKey(V4_STORAGE.ACCOUNT_ID);
    removeStorageKey(V4_STORAGE.CODE_VERIFIER);
    removeStorageKey(V4_STORAGE.AUTH_MODE);
    removeStorageKey(V4_STORAGE.ACCOUNTS);
    ssRemove(V4_STORAGE.CODE_VERIFIER);
    ssRemove(V4_STORAGE.STATE);
}

export function isV4AuthActive() {
    return getStorage(V4_STORAGE.AUTH_MODE) === 'v4';
}

export function isV4SyntheticToken(token) {
    return typeof token === 'string' && token.indexOf('v4:') === 0;
}

export function buildV4TokenListFromAccounts(accounts, activeAccountId) {
    if (!Array.isArray(accounts)) {
        return [];
    }
    return accounts.map(acc => {
        const id = acc.account_id;
        const isDemo = (acc.account_type || '').toLowerCase() === 'demo';
        return {
            accountName: id,
            token      : `v4:${id}`,
            loginInfo  : {
                loginid             : id,
                is_virtual          : isDemo ? 1 : 0,
                currency            : acc.currency || 'USD',
                landing_company_name: 'svg',
                country             : '',
                account_list        : [],
            },
            hasRealityCheck   : false,
            hasTradeLimitation: false,
        };
    });
}

export function applyV4AccountsToStorage(accounts, activeAccountId) {
    const list = buildV4TokenListFromAccounts(accounts, activeAccountId);
    setTokenList(list);
    const active = activeAccountId || (accounts[0] && accounts[0].account_id) || '';
    if (active) {
        setStorage(AppConstants.STORAGE_ACTIVE_TOKEN, `v4:${active}`);
        setStorage(V4_STORAGE.ACCOUNT_ID, active);
    }
    setStorage(V4_STORAGE.ACCOUNTS, JSON.stringify(accounts));
}

async function completeV4OAuthFromCallback(code, receivedState) {
    const dbg = (...args) => console.log('[oauth-debug]', ...args);
    dbg(`appId: completeV4OAuthFromCallback codeLen=${code && code.length} receivedState=${receivedState || 'none'}`);

    // Verify state before anything else — CSRF guard
    const storedState = ssGet(V4_STORAGE.STATE);
    ssRemove(V4_STORAGE.STATE);
    dbg(`appId: CSRF check storedState=${storedState || 'none'} receivedState=${receivedState || 'none'} match=${storedState === receivedState}`);
    if (storedState && receivedState && storedState !== receivedState) {
        dbg('appId: CSRF MISMATCH — throwing');
        throw new Error('OAuth state mismatch — possible CSRF attack');
    }

    // Sync Supabase session before doing anything — verifier may be stored there
    dbg('appId: calling initAutotraderSession...');
    await initAutotraderSession();
    dbg('appId: initAutotraderSession done');

    // sessionStorage primary (survives redirect, cleared on tab close)
    const ssVerifier = ssGet(V4_STORAGE.CODE_VERIFIER);
    const lsVerifier = getStorage(V4_STORAGE.CODE_VERIFIER);
    dbg(`appId: verifier lookup — sessionStorage=${ssVerifier ? 'FOUND' : 'missing'} localStorage=${lsVerifier ? 'FOUND' : 'missing'}`);
    let verifier = ssVerifier || lsVerifier;
    if (!verifier) {
        // Fallback: read from Supabase user metadata (stored during redirectToV4OAuthLogin)
        dbg('appId: verifier not in storage, trying Supabase metadata...');
        try {
            const {
                data: { session },
            } = await supabase.auth.getSession();
            const meta = session && session.user && session.user.user_metadata;
            dbg(`appId: Supabase metadata session=${!!session} hasVerifierKey=${!!(meta && meta.v4_code_verifier)}`);
            if (meta) {
                verifier = meta.v4_code_verifier || '';
            }
        } catch (e) {
            dbg(`appId: Supabase metadata fetch error: ${e && e.message}`);
        }
    }
    if (!verifier) {
        dbg('appId: MISSING PKCE VERIFIER — throwing');
        throw new Error('Missing PKCE verifier — start login again.');
    }
    dbg('appId: verifier found, calling POST /api/oauth/exchange...');
    const exchangeUrl = apiUrl('/api/oauth/exchange');
    const exRes = await autotraderFetch(exchangeUrl, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ code, verifier }),
    });
    const ex = { ok: exRes.ok, status: exRes.status, data: await exRes.json().catch(() => ({})) };
    dbg(`appId: exchange response ok=${ex.ok} error=${ex.data.error || 'none'} status=${ex.status}`);
    if (!ex.ok) {
        dbg(`appId: exchange FAILED — ${ex.data && ex.data.error}`);
        throw new Error((ex.data && ex.data.error) || 'OAuth exchange failed');
    }
    console.log('[oauth] ✓ bearer token saved server-side in DB (exchange API returned ok=true)');
    ssRemove(V4_STORAGE.CODE_VERIFIER);
    removeStorageKey(V4_STORAGE.CODE_VERIFIER);

    dbg('appId: exchange complete, calling GET /api/oauth/account-info...');

    const infoUrl = apiUrl('/api/oauth/account-info');
    const infoRes = await autotraderFetch(infoUrl);
    const info = { ok: infoRes.ok, status: infoRes.status, data: await infoRes.json().catch(() => ({})) };
    dbg(`appId: account-info response ok=${info.ok} selectedId=${info.data && info.data.selected && info.data.selected.account_id} accountCount=${info.data && info.data.available && info.data.available.length} error=${(info.data && info.data.error) || 'none'}`);
    if (!info.ok || !info.data.selected) {
        dbg(`appId: account-info FAILED — ${info.data && info.data.error}`);
        throw new Error((info.data && info.data.error) || 'account-info failed');
    }
    console.log('[oauth] ✓ bearer token read from DB server-side — account-info returned', info.data.available && info.data.available.length, 'accounts');
    const selectedId = info.data.selected.account_id;
    setStorage(V4_STORAGE.ACCOUNT_ID, selectedId);
    setStorage(V4_STORAGE.AUTH_MODE, 'v4');

    removeAllTokens();
    applyV4AccountsToStorage(info.data.available, selectedId);
    dbg(`appId: v4 session stored accountId=${selectedId} accounts=${info.data.available && info.data.available.length}`);
}

/** When Deriv redirects to any page with ?code= (e.g. bot.html), run PKCE exchange + persist session. */
export async function finalizeV4OAuthWithCode(code, state) {
    await completeV4OAuthFromCallback(code, state);
}

export async function rehydrateV4SessionIfNeeded() {
    const active = isV4AuthActive();
    const tokenCount = getTokenList().length;
    console.log('[oauth] rehydrate — v4Active:', active, 'tokenCount:', tokenCount);

    if (active && tokenCount > 0) {
        console.log('[oauth] rehydrate — tokens present, skip');
        return;
    }

    if (!active) {
        // AUTH_MODE cleared (e.g. cache wipe) — check if Supabase session exists before giving up
        let hasSession = false;
        try {
            const {
                data: { session },
            } = await supabase.auth.getSession();
            hasSession = !!session;
            console.log('[oauth] rehydrate — no AUTH_MODE, supabase session:', hasSession ? 'YES' : 'NO', session ? `email=${session.user && session.user.email}` : '');
        } catch (e) {
            console.log('[oauth] rehydrate — supabase check failed:', e && e.message);
        }
        if (!hasSession) {
            console.log('[oauth] rehydrate — no session, skip');
            return;
        }
        console.log('[oauth] rehydrate — has supabase session without AUTH_MODE, attempting server fetch...');
    }

    const accountId = getStorage(V4_STORAGE.ACCOUNT_ID);
    console.log('[oauth] rehydrate — accountId from storage:', accountId || 'none');

    const url = apiUrl(`/api/oauth/account-info${accountId ? `?${new URLSearchParams({ accountId })}` : ''}`);
    console.log('[oauth] rehydrate — calling account-info...');
    const infoRes = await autotraderFetch(url);
    const info = { ok: infoRes.ok, status: infoRes.status, data: await infoRes.json().catch(() => ({})) };
    console.log('[oauth] rehydrate — account-info ok:', info.ok, 'selectedId:', info.data && info.data.selected && info.data.selected.account_id, 'available:', info.data && info.data.available && info.data.available.length, 'error:', (info.data && info.data.error) || 'none');

    if (!info.ok || !info.data || !info.data.available) {
        console.log('[oauth] rehydrate — account-info failed (bearer missing from DB?), clearing v4 session');
        clearV4Session();
        return;
    }
    console.log('[oauth] ✓ bearer token read from DB server-side — rehydrate got', info.data.available.length, 'accounts');
    setStorage(V4_STORAGE.AUTH_MODE, 'v4');
    const selectedId = (info.data.selected && info.data.selected.account_id) || accountId;
    applyV4AccountsToStorage(info.data.available, selectedId);
    console.log('[oauth] rehydrate — restored v4 session, accountId:', selectedId, 'accounts:', info.data.available.length);
}

export const queryToObjectArray = queryStr => {
    const tokens = [];
    Object.keys(queryStr).forEach(o => {
        if (!/\d$/.test(o)) return;
        const index = parseInt(o.slice(-1), 10);
        let key = o.slice(0, -1);
        key = key === 'acct' ? 'accountName' : key;
        if (index <= tokens.length) {
            tokens[index - 1][key] = queryStr[o];
        } else {
            tokens.push({});
            tokens[index - 1][key] = queryStr[o];
        }
    });
    return tokens;
};

export const oauthLogin = (done = () => 0) => {
    const queryStr = parseQueryString();

    if (queryStr.code) {
        $('#main').hide();
        completeV4OAuthFromCallback(String(queryStr.code))
            .then(() => {
                document.location = 'bot.html';
            })
            .catch(() => {
                clearV4Session();
                removeAllTokens();
                done();
            });
        return;
    }

    const tokenObjectList = queryToObjectArray(queryStr);
    if (tokenObjectList.length) {
        clearV4Session();
        $('#main').hide();
        addTokenIfValid(tokenObjectList[0].token, tokenObjectList).then(() => {
            const accounts = getTokenList();
            if (accounts.length) {
                setStorage(AppConstants.STORAGE_ACTIVE_TOKEN, accounts[0].token);
            }
            document.location = 'bot.html';
        });
    } else {
        done();
    }
};

export const getCustomEndpoint = () => ({
    url  : getStorage('config.server_url'),
    appId: getStorage('config.app_id'),
});

export const getDefaultEndpoint = () => ({
    url  : 'api.derivws.com',
    appId: 37985,
});

export const getServerAddressFallback = () => getDefaultEndpoint().url;

export const getAppIdFallback = () => getDefaultEndpoint().appId;

/** Public market-data / unauthenticated Options WS (new Deriv API). */
export const getWebSocketURL = () => 'wss://api.derivws.com/trading/v1/options/ws/public';

/** Prefer public Options WS; custom hosts are no longer supported on legacy v3. */
export const generateWebSocketURL = _serverUrl =>
    // Always use the new public Options WebSocket endpoint
    'wss://api.derivws.com/trading/v1/options/ws/public';

const options = {
    apiUrl   : getWebSocketURL(),
    rawApiUrl: true,
    get language() {
        return getLanguage().toUpperCase();
    },
    appId    : 71495,
    keepAlive: true,
};

export const getOptions = () => options;

export const generateLiveApiInstance = () => new LiveApi(options);

export const generateTestLiveApiInstance = overrideOptions => new LiveApi(Object.assign({}, options, overrideOptions));

export function makeV4OnReconnect() {
    return async () => {
        const accountId = getStorage(V4_STORAGE.ACCOUNT_ID);
        if (!accountId) {
            throw new Error('Missing v4 accountId for reconnect');
        }
        const url = apiUrl(`/api/oauth/websocket-url?${new URLSearchParams({ accountId, otpOnly: '1' })}`);
        const resRes = await autotraderFetch(url);
        const res = { ok: resRes.ok, data: await resRes.json().catch(() => ({})) };
        if (!res.ok || !res.data.websocketUrl) {
            throw new Error((res.data && res.data.error) || 'Reconnect URL failed');
        }
        if (res.data.accounts) {
            setStorage(V4_STORAGE.ACCOUNTS, JSON.stringify(res.data.accounts));
        }
        return res.data.websocketUrl;
    };
}

export async function fetchV4WsUrl(accountId, { otpOnly = false } = {}) {
    const params = { accountId };
    if (otpOnly) params.otpOnly = '1';
    const url = apiUrl(`/api/oauth/websocket-url?${new URLSearchParams(params)}`);
    const resRes = await autotraderFetch(url);
    const res = { ok: resRes.ok, data: await resRes.json().catch(() => ({})) };
    if (!res.ok || !res.data || !res.data.websocketUrl) {
        throw new Error((res.data && res.data.error) || 'Failed to get v4 WS URL');
    }
    return res.data.websocketUrl;
}

export async function createTradingLiveApi(AppConstantsRef) {
    if (!isV4AuthActive()) {
        return generateLiveApiInstance();
    }
    const accountId = getStorage(V4_STORAGE.ACCOUNT_ID);
    console.log('[oauth] createTradingLiveApi — accountId:', accountId);
    if (!accountId) {
        throw new Error('V4 active but no accountId stored.');
    }
    const url = apiUrl(
        `/api/oauth/websocket-url?${new URLSearchParams({
            accountId,
        })}`
    );
    const resRes = await autotraderFetch(url);
    const res = { ok: resRes.ok, data: await resRes.json().catch(() => ({})) };

    if (!res.ok || !res.data || !res.data.websocketUrl) {
        throw new Error((res.data && res.data.error) || 'Failed to fetch V4 websocket url');
    }
    // We do NOT call `waitForV4LoginidAuthorize` manually here; instead View.js will mock the authorize payload.
    return new LiveApi({
        apiUrl     : res.data.websocketUrl,
        rawApiUrl  : true,
        language   : getLanguage().toUpperCase(),
        appId      : V4_APP_ID,
        keepAlive  : true,
        onReconnect: makeV4OnReconnect(),
    });
}

export async function redirectToV4OAuthLogin() {
    const dbg = (...args) => console.log('[oauth-debug]', ...args);
    dbg('[oauth] redirectToV4OAuthLogin START — building PKCE flow');
    console.log('[oauth] Starting PKCE flow — client_id:', V4_CLIENT_ID, 'redirect_uri:', V4_REDIRECT_URI);
    const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    const { url, codeVerifier } = await OAuth.getAuthUrl(V4_CLIENT_ID, V4_REDIRECT_URI, 'trade account_manage', getAppIdFallback());
    const authUrl = url.includes('state=') ? url.replace(/([?&])state=[^&]*/, `$1state=${encodeURIComponent(state)}`) : `${url}&state=${encodeURIComponent(state)}`;
    dbg(`[oauth] authUrl=${authUrl.slice(0, 100)}... state=${state.slice(0, 8)}... verifierLen=${codeVerifier && codeVerifier.length}`);
    ssSet(V4_STORAGE.CODE_VERIFIER, codeVerifier);
    ssSet(V4_STORAGE.STATE, state);
    setStorage(V4_STORAGE.CODE_VERIFIER, codeVerifier); // localStorage fallback
    dbg('[oauth] verifier stored in sessionStorage + localStorage');
    try {
        const {
            data: { session },
        } = await supabase.auth.getSession();
        if (session) {
            dbg('[oauth] storing verifier in Supabase user metadata (session exists)');
            await supabase.auth.updateUser({ data: { v4_code_verifier: codeVerifier } });
        } else {
            dbg('[oauth] no Supabase session — verifier only in sessionStorage/localStorage');
        }
    } catch (e) {
        dbg(`[oauth] Supabase metadata write failed: ${e && e.message}`);
    }
    dbg('[oauth] redirecting to Deriv...');
    window.location = authUrl;
}

export function waitForV4LoginidAuthorize(api) {
    return new Promise((resolve, reject) => {
        const finish = auth => {
            if (auth && auth.loginid) {
                return auth;
            }
            return null;
        };
        const tryNow = api.userAuth && api.userAuth.authorize && finish(api.userAuth.authorize);
        if (tryNow) {
            resolve(tryNow);
            return;
        }
        const timer = setTimeout(() => {
            api.events.off('authorize', handler);
            reject(new Error('Authorization timeout'));
        }, 25000);
        const handler = pack => {
            const a = pack && pack.authorize;
            if (finish(a)) {
                clearTimeout(timer);
                api.events.off('authorize', handler);
                resolve(a);
            }
        };
        api.events.on('authorize', handler);
    });
}

export async function addTokenIfValid(token, tokenObjectList) {
    const api = generateLiveApiInstance();
    try {
        const { authorize } = await api.authorize(token);
        const { landing_company_name: lcName } = authorize;
        const {
            landing_company_details: { has_reality_check: hasRealityCheck },
        } = await api.getLandingCompanyDetails(lcName);
        addToken(token, authorize, !!hasRealityCheck, ['iom', 'malta'].includes(lcName) && authorize.country === 'gb');

        const { account_list: accountList } = authorize;
        if (accountList.length > 1) {
            tokenObjectList.forEach(tokenObject => {
                if (tokenObject.token !== token) {
                    const account = accountList.filter(o => o.loginid === tokenObject.accountName);
                    if (account.length) {
                        addToken(tokenObject.token, account[0], false, false);
                    }
                }
            });
        }
    } catch (e) {
        removeToken(tokenObjectList[0].token);
        GTM.setVisitorId();
        throw e;
    }
    return api.disconnect();
}

export const logoutAllTokens = () =>
    new Promise(resolve => {
        clearAutotraderSession();
        clearV4Session();
        // Legacy tokens can no longer authorize (Deriv's current API has no WebSocket
        // authorize message), so there's no server-side session to invalidate — just
        // clear local state instead of attempting a doomed authorize+logOut round trip.
        removeAllTokens();
        resolve();
    });
